import { forwardRef, useState, useCallback, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { Input } from '@/components/base/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Select';
import { SubcategoryPill } from '@/components/display/SubcategoryPill';
import type {
  FiltersContent,
  FilterState,
  CategoryOption,
} from '@/types/service-directory';

// Import icons
import GoldenBuckeyeIcon from '@/icons/GoldenBuckeye.svg?react';
import CheckboxCheckedIcon from '@/icons/CheckboxChecked.svg?react';
import CheckboxUncheckedIcon from '@/icons/CheckboxUnchecked.svg?react';

/**
 * Props interface for FiltersSidebar component
 */
export interface FiltersSidebarProps {
  /** Filters content */
  content?: FiltersContent;
  /** Available categories (from data) */
  categories: CategoryOption[];
  /** Current filter state */
  filterState: FilterState;
  /** IDs of subcategories that should be disabled (no services in current search area) */
  disabledSubcategories?: string[];
  /** Callback when filter state changes */
  onFilterChange: (state: Partial<FilterState>) => void;
  /** Callback when apply filters is clicked */
  onApplyFilters: () => void;
  /** Callback when feedback form is clicked (non-functional) */
  onOpenFeedback?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Collapsible section header component
 */
function CollapsibleHeader({
  title,
  subtitle,
  isExpanded,
  onToggle,
  id,
}: {
  title: string;
  subtitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
  id: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      aria-controls={id}
      className={cn(
        'w-full flex items-start justify-between',
        'text-left cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
      )}
    >
      <div className="flex flex-col">
        <span
          className={cn(
            typography.subtitle,
            'text-foreground'
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span
            className={cn(
              'text-sm leading-5',
              "font-['Source_Sans_Pro']",
              'text-[var(--color-text-secondary)]'
            )}
          >
            {subtitle}
          </span>
        )}
      </div>
      {isExpanded ? (
        <ChevronUp className="h-5 w-5 text-foreground shrink-0" aria-hidden="true" />
      ) : (
        <ChevronDown className="h-5 w-5 text-foreground shrink-0" aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * FiltersSidebar - Left sidebar with filter controls
 *
 * Contains location/distance filter, category filter with subcategory pills,
 * Golden Buckeye checkbox, apply button, and feedback prompt.
 *
 * @example
 * ```tsx
 * import { FiltersSidebar } from '@/components';
 *
 * <FiltersSidebar
 *   content={filtersContent}
 *   categories={categories}
 *   filterState={filterState}
 *   onFilterChange={handleFilterChange}
 *   onApplyFilters={handleApplyFilters}
 * />
 * ```
 */
export const FiltersSidebar = forwardRef<HTMLDivElement, FiltersSidebarProps>(
  (
    {
      content,
      categories,
      filterState,
      disabledSubcategories = [],
      onFilterChange,
      onApplyFilters,
      onOpenFeedback: _onOpenFeedback,
      className,
    },
    ref
  ) => {
    // Collapsible section states
    const [locationExpanded, setLocationExpanded] = useState(true);
    const [categoryExpanded, setCategoryExpanded] = useState(true);

    // Get current category's subcategories and name (memoized to prevent unnecessary re-renders)
    const currentCategory = useMemo(() => {
      return categories.find((c) => c.id === filterState.selectedCategory);
    }, [categories, filterState.selectedCategory]);

    const currentSubcategories = currentCategory?.subcategories || [];

    // Handlers
    const handleLocationChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange({ location: e.target.value });
      },
      [onFilterChange]
    );

    const handleLocationKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onApplyFilters();
        }
      },
      [onApplyFilters]
    );

    const handleDistanceChange = useCallback(
      (value: string) => {
        onFilterChange({ distance: value });
      },
      [onFilterChange]
    );

    const handleCategoryChange = useCallback(
      (value: string) => {
        const categoryId = value === 'all' ? null : value;
        // Auto-select all subcategories by default
        // When a specific category is selected, select all its subcategories
        // When "All categories" is selected, select all subcategories across all categories
        const selectedCategory = categories.find((c) => c.id === categoryId);
        const defaultSubcategories = selectedCategory
          ? selectedCategory.subcategories.map((sub) => sub.id)
          : categories.flatMap((cat) => cat.subcategories.map((sub) => sub.id));
        onFilterChange({
          selectedCategory: categoryId,
          selectedSubcategories: defaultSubcategories,
        });
      },
      [categories, onFilterChange]
    );

    const handleSubcategoryToggle = useCallback(
      (subcategoryId: string) => {
        const isSelected = filterState.selectedSubcategories.includes(subcategoryId);
        const newSubcategories = isSelected
          ? filterState.selectedSubcategories.filter((id) => id !== subcategoryId)
          : [...filterState.selectedSubcategories, subcategoryId];
        onFilterChange({ selectedSubcategories: newSubcategories });
      },
      [filterState.selectedSubcategories, onFilterChange]
    );

    const handleSelectAll = useCallback(() => {
      // If "All categories" is selected, select all subcategories from all categories
      // Otherwise, select only the current category's subcategories
      const subcategoriesToSelect = filterState.selectedCategory === null
        ? categories.flatMap((cat) => cat.subcategories.map((sub) => sub.id))
        : currentSubcategories.map((sub) => sub.id);
      onFilterChange({
        selectedSubcategories: subcategoriesToSelect,
      });
    }, [categories, currentSubcategories, filterState.selectedCategory, onFilterChange]);

    const handleDeselectAll = useCallback(() => {
      onFilterChange({ selectedSubcategories: [] });
    }, [onFilterChange]);

    const handleGoldenBuckeyeToggle = useCallback(() => {
      onFilterChange({ includeGoldenBuckeye: !filterState.includeGoldenBuckeye });
    }, [filterState.includeGoldenBuckeye, onFilterChange]);

    if (!content) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'w-[320px] shrink-0',
          'bg-white rounded-l',
          'border-t border-b border-l border-[var(--color-border-default)]',
          'p-4',
          'flex flex-col',
          'overflow-x-hidden',
          className
        )}
        role="region"
        aria-label="Filters"
      >
        {/* Filters title */}
        <h2
          className={cn(
            typography.headingSmall,
            'mb-4 text-foreground shrink-0'
          )}
        >
          {content.title}
        </h2>

        {/* Location and distance filter */}
        <div className="border-b border-[var(--color-border-default)] pb-6 mb-6 shrink-0">
          <CollapsibleHeader
            title={content.locationFilter.title}
            subtitle={content.locationFilter.subtitle}
            isExpanded={locationExpanded}
            onToggle={() => setLocationExpanded(!locationExpanded)}
            id="location-filter"
          />
          {locationExpanded && (
            <div id="location-filter" className="mt-3 flex flex-col gap-3">
              {/* Address input */}
              <Input
                type="text"
                placeholder={content.locationFilter.addressPlaceholder}
                value={filterState.location}
                onChange={handleLocationChange}
                onKeyDown={handleLocationKeyDown}
                aria-label="Location address or zip code"
                className="w-full input-service-directory"
              />

              {/* Distance dropdown */}
              <Select
                value={filterState.distance}
                onValueChange={handleDistanceChange}
              >
                <SelectTrigger className="w-full input-service-directory" aria-label="Select distance">
                  <SelectValue placeholder={content.locationFilter.defaultDistance} />
                </SelectTrigger>
                <SelectContent>
                  {content.locationFilter.distanceOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Category filter */}
        <div className="border-b border-[var(--color-border-default)] mb-4 flex flex-col min-h-0">
          <CollapsibleHeader
            title={content.categoryFilter.title}
            isExpanded={categoryExpanded}
            onToggle={() => setCategoryExpanded(!categoryExpanded)}
            id="category-filter"
          />
          {categoryExpanded && (
            <div id="category-filter" className="mt-3 flex flex-col gap-3 min-h-0">
              {/* Category dropdown */}
              <Select
                value={filterState.selectedCategory || 'all'}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger className="w-full input-service-directory" aria-label="Select category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Subcategories section */}
              {(filterState.selectedCategory === null || currentSubcategories.length > 0) && (
                <div className="mt-2 flex flex-col min-h-0">
                  {/* Subcategories header */}
                  <div className="flex items-center justify-between pb-3 border-b border-[var(--color-border-default)]">
                    <span
                      className={cn(
                        typography.buttonDefault,
                        'text-foreground'
                      )}
                    >
                      {content.categoryFilter.subcategoriesTitle}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className={cn(
                          typography.buttonDefault,
                          'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                          'hover:underline cursor-pointer',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
                        )}
                      >
                        {content.categoryFilter.selectAllText}
                      </button>
                      <span className="w-px h-[30px] bg-[#EBEBEB]" aria-hidden="true"></span>
                      <button
                        type="button"
                        onClick={handleDeselectAll}
                        className={cn(
                          typography.buttonDefault,
                          'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
                          'hover:underline cursor-pointer',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
                        )}
                      >
                        {content.categoryFilter.deselectAllText}
                      </button>
                    </div>
                  </div>

                  {/* Category name(s) and subcategory pills - scrollable area */}
                  <div className="pt-3 pb-4 overflow-y-auto min-h-0 pr-2 max-h-[600px]">
                    {filterState.selectedCategory === null ? (
                      /* Show all categories with their subcategories */
                      <div className="space-y-4">
                        {categories.map((category) => (
                          <div key={category.id}>
                            <span
                              className={cn(
                                typography.buttonDefault,
                                'text-black block mb-2'
                              )}
                            >
                              {category.name}
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {category.subcategories.map((subcategory) => (
                                <SubcategoryPill
                                  key={subcategory.id}
                                  subcategory={subcategory}
                                  isSelected={filterState.selectedSubcategories.includes(
                                    subcategory.id
                                  )}
                                  isDisabled={disabledSubcategories.includes(subcategory.id)}
                                  onClick={() => handleSubcategoryToggle(subcategory.id)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Show single category with its subcategories */
                      <>
                        {currentCategory && (
                          <span
                            className={cn(
                              typography.buttonDefault,
                              'text-black block mb-2'
                            )}
                          >
                            {currentCategory.name}
                          </span>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {currentSubcategories.map((subcategory) => (
                            <SubcategoryPill
                              key={subcategory.id}
                              subcategory={subcategory}
                              isSelected={filterState.selectedSubcategories.includes(
                                subcategory.id
                              )}
                              isDisabled={disabledSubcategories.includes(subcategory.id)}
                              onClick={() => handleSubcategoryToggle(subcategory.id)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Golden Buckeye filter */}
        <div className="mb-6 shrink-0">
          <div
            role="checkbox"
            aria-checked={filterState.includeGoldenBuckeye}
            tabIndex={0}
            onClick={handleGoldenBuckeyeToggle}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleGoldenBuckeyeToggle();
              }
            }}
            className={cn(
              'flex items-start gap-3 cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded p-1 -m-1'
            )}
          >
            {/* Checkbox */}
            <div className="mt-0.5 shrink-0">
              {filterState.includeGoldenBuckeye ? (
                <CheckboxCheckedIcon className="w-5 h-5" aria-hidden="true" />
              ) : (
                <CheckboxUncheckedIcon className="w-5 h-5" aria-hidden="true" />
              )}
            </div>

            {/* Label with icon */}
            <div className="flex items-start gap-2">
              <GoldenBuckeyeIcon
                className="h-6 w-6 text-[var(--color-golden-buckeye)] shrink-0"
                aria-hidden="true"
              />
              <span
                className={cn(
                  typography.buttonDefault,
                  'text-black select-none'
                )}
              >
                {content.goldenBuckeyeFilter.text}
              </span>
            </div>
          </div>
        </div>

        {/* Apply filters button */}
        <Button
          variant="primary"
          size="large"
          content={{ text: content.applyButton.text }}
          onClick={onApplyFilters}
          disabled={!filterState.location.trim()}
          className="w-full mb-4 shrink-0"
        />

        {/* Feedback prompt */}
        <div
          className={cn(
            'w-[288px] p-4',
            'bg-white rounded',
            'border border-[#EBEBEB]',
            'shadow-[0px_4px_4px_0px_#BBBBBB40]',
            'shrink-0'
          )}
        >
          <h3
            className={cn(
              typography.headingSmall,
              'text-black'
            )}
          >
            {content.feedbackPrompt.title}
          </h3>
          <p
            className={cn(
              typography.body,
              'text-black mt-6'
            )}
          >
            {content.feedbackPrompt.description}
          </p>
          <Button
            className="mt-6"
            variant="secondary"
            size="default"
            content={{
              text: content.feedbackPrompt.button.text,
              icon: 'ArrowRight',
            }}
            onClick={() => window.open('https://www.surveymonkey.com/r/FeedbackForResourceHub', '_blank', 'noopener,noreferrer')}
            aria-label={content.feedbackPrompt.button.text}
          />
        </div>
      </div>
    );
  }
);

FiltersSidebar.displayName = 'FiltersSidebar';
